import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import type { ReaderTag } from '@/api/client';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import { EditableCellPopoverHeader } from '@/components/ProjectsTable/EditableCellPopoverHeader';
import { ReaderCatalogTagChips } from '@/components/ReadersTable/ReaderCatalogTagChips';
import { CatalogTagsInlineBadgesRow } from './CatalogTagsInlineBadgesRow';
import { CatalogTagsInlineMeasurer } from './CatalogTagsInlineMeasurer';
import { useCatalogTagsInlineFit } from './useCatalogTagsInlineFit';
import './CatalogTagsInlineBadgesRow.css';
import '@/components/ProjectsTable/EditableCell.css';
import '@/components/ReadersTable/readerCatalogTagPill.css';

const TAGS_POPOVER_WIDTH = 400;

export interface CatalogTagsTableCellDisplayProps {
  tags: ReaderTag[] | undefined;
  disabled?: boolean;
  /** Подпись в заголовке попапа: «Теги: …» (ФИО чтеца или название проекта в фонотеке). */
  popoverSubjectName?: string;
}

export function CatalogTagsTableCellDisplay({
  tags,
  disabled = false,
  popoverSubjectName = '',
}: CatalogTagsTableCellDisplayProps) {
  const list = tags ?? [];
  const rowRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = rowRef as React.RefObject<HTMLElement | null>;
  const popoverRef = useRef<HTMLDivElement>(null);
  const positionAnchorRef = useRef<HTMLButtonElement | null>(null);

  const [popoverOpen, setPopoverOpen] = useState(false);

  const { fit, measurerRef } = useCatalogTagsInlineFit(containerRef, list);

  const {
    shouldRender,
    contentClasses,
    requestClose: requestPopoverClose,
    isExiting,
  } = useOverlayAnimation(
    popoverOpen,
    useCallback(() => setPopoverOpen(false), []),
    true
  );

  if (popoverOpen && rowRef.current) {
    positionAnchorRef.current = rowRef.current;
  }
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  triggerRef.current = popoverOpen ? rowRef.current : positionAnchorRef.current;

  usePopoverPositioning({
    triggerRef: triggerRef as React.RefObject<HTMLElement | null>,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1001,
    onAnchorDetached: requestPopoverClose,
    dependencies: [list.length, fit.visibleCount],
  });

  useClickOutside(popoverRef, requestPopoverClose as (e: MouseEvent) => void, {
    excludeRefs: [rowRef],
    enabled: shouldRender,
    capture: false,
  });

  const openPopover = useCallback(() => {
    if (disabled || list.length === 0) return;
    setPopoverOpen(true);
  }, [disabled, list.length]);

  const popoverTitle = popoverSubjectName.trim()
    ? `Теги: ${popoverSubjectName.trim()}`
    : 'Теги';

  const hasAnchor = !!triggerRef.current;
  const popoverAnchorStyle: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: isExiting ? 'none' : 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const popoverPanelStyle: React.CSSProperties = {
    width: TAGS_POPOVER_WIDTH,
    minWidth: TAGS_POPOVER_WIDTH,
    maxWidth: 'calc(100vw - 16px)',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  if (list.length === 0) {
    return (
      <div className="catalog-tags-table-cell-display readers-table-catalog__tags-cell">
        <div className="catalog-tags-table-cell-display__empty">
          <span className="projects-table__empty-value">N/A</span>
        </div>
      </div>
    );
  }

  return (
    <div className="catalog-tags-table-cell-display">
      <CatalogTagsInlineMeasurer measurerRef={measurerRef} tags={list} />
      <CatalogTagsInlineBadgesRow
        tags={list}
        fit={fit}
        disabled={disabled}
        onClick={openPopover}
        containerRef={rowRef}
      />
      {shouldRender &&
        createPortal(
          <div ref={popoverRef} style={popoverAnchorStyle}>
            <OverlayIntrinsicSize>
              <div
                style={popoverPanelStyle}
                className={clsx('editable-cell-popover catalog-tags-list-popover', contentClasses)}
              >
                <EditableCellPopoverHeader onClose={requestPopoverClose}>{popoverTitle}</EditableCellPopoverHeader>
                <div className="editable-cell-popover__form">
                  <ReaderCatalogTagChips tags={list} comfortable />
                </div>
              </div>
            </OverlayIntrinsicSize>
          </div>,
          document.body
        )}
    </div>
  );
}
